<template>
  <div class="team-page">
    <van-nav-bar
      title="我的团队"
      fixed
      placeholder
      :border="false"
      left-arrow
      @click-left="onClickLeft"
    />

    <main class="team-content">
      <section class="hero-card">
        <span class="eyebrow">MY TEAM</span>
        <h1>团队与收益</h1>
        <p>按一代、二代、三代查看成员投资产出及本人实际获得的返佣。</p>

        <form class="search-box" @submit.prevent="searchTeam">
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="搜索账号、手机号、邮箱或邀请码"
          />
          <button type="submit" :disabled="loading || fundRecordLoading">
            {{ loading ? "查询中" : "搜索" }}
          </button>
        </form>
        <p class="scope-tip">仅可搜索和查看您本人及您名下的团队成员</p>
      </section>

      <section v-if="root" class="root-card">
        <div class="root-main">
          <div class="avatar">{{ avatarText(root) }}</div>
          <div>
            <div class="root-badges">
              <span class="generation-badge">团队负责人</span>
              <span :class="['role-badge', { agent: Number(root.sale) === 1 }]">
                {{ roleText(root) }}
              </span>
              <span v-if="Number(root.id) === Number(scopeOwnerId)" class="self-badge">本人</span>
            </div>
            <h2>{{ memberName(root) }}</h2>
            <p>账号：{{ accountText(root) }}</p>
            <p>邀请码：{{ root.invite || "-" }}</p>
          </div>
        </div>
        <button
          v-if="Number(root.id) !== Number(scopeOwnerId)"
          class="back-self"
          @click="loadRoot(scopeOwnerId)"
        >
          返回我的团队
        </button>
      </section>

      <section v-if="root" class="summary-grid">
        <div>
          <span>直属下级</span>
          <strong>{{ summary.direct_count || 0 }}</strong>
        </div>
        <div>
          <span>全部下级</span>
          <strong>{{ summary.descendant_count || 0 }}</strong>
        </div>
        <div>
          <span>下级代理</span>
          <strong>{{ summary.agent_count || 0 }}</strong>
        </div>
        <div>
          <span>下级用户</span>
          <strong>{{ summary.user_count || 0 }}</strong>
        </div>
        <div>
          <span>团队层级</span>
          <strong>{{ summary.max_generation || 0 }}代</strong>
        </div>
      </section>

      <section
        v-if="root && Number(root.id) === Number(scopeOwnerId)"
        class="earnings-section"
      >
        <div class="section-title">
          <div>
            <span>TEAM EARNINGS</span>
            <h2>团队收益明细</h2>
          </div>
          <em>实际到账数据</em>
        </div>
        <div class="earnings-total">
          <div>
            <span>团队累计投资产出</span>
            <strong>{{ formatMoney(earningsSummary.total_investment) }} USD</strong>
          </div>
          <div>
            <span>我的累计返佣利润</span>
            <strong>{{ formatMoney(earningsSummary.total_commission) }} USD</strong>
          </div>
        </div>
        <article
          v-for="level in generationEarnings"
          :key="level.level"
          class="generation-income-card"
        >
          <div class="generation-name">
            <strong>{{ chineseGeneration(level.level) }}</strong>
            <span>{{ level.level === 1 ? "直属成员" : `${chineseGeneration(level.level - 1)}成员邀请` }}</span>
          </div>
          <div class="generation-data">
            <p><span>成员人数</span><strong>{{ level.people || 0 }} 人</strong></p>
            <p><span>累计投资产出</span><strong>{{ formatMoney(level.investment) }} USD</strong></p>
            <p><span>返佣比例</span><strong>{{ formatRate(level.rate) }}%</strong></p>
            <p class="profit"><span>我的实际利润</span><strong>+{{ formatMoney(level.commission) }} USD</strong></p>
          </div>
        </article>
        <p class="earnings-note">
          统计口径：成员完成产品购买后计入累计投资；返佣利润以本人账户实际到账记录为准。
        </p>
      </section>

      <section v-if="root" class="fund-record-section">
        <div class="section-title">
          <div>
            <span>TEAM FUND RECORDS</span>
            <h2>团队成员资金记录</h2>
          </div>
          <em>共 {{ fundRecordSummary.record_count || 0 }} 笔</em>
        </div>

        <div class="fund-record-summary">
          <span>已完成{{ fundRecordType === "recharge" ? "充值" : "提现" }}</span>
          <strong>{{ formatMoney(fundRecordSummary.approved_amount) }} USD</strong>
        </div>

        <div class="fund-record-tabs">
          <button
            type="button"
            :class="{ active: fundRecordType === 'recharge' }"
            :disabled="fundRecordLoading"
            @click="switchFundRecordType('recharge')"
          >
            充值记录
          </button>
          <button
            type="button"
            :class="{ active: fundRecordType === 'withdraw' }"
            :disabled="fundRecordLoading"
            @click="switchFundRecordType('withdraw')"
          >
            提现记录
          </button>
        </div>

        <div v-if="fundRecords.length" class="fund-record-list">
          <article
            v-for="item in fundRecords"
            :key="`${fundRecordType}-${item.id}`"
            class="fund-record-card"
          >
            <div class="fund-record-main">
              <div class="member-avatar">{{ avatarText(item) }}</div>
              <div class="fund-record-member">
                <strong>{{ memberName(item) }}</strong>
                <span>{{ accountText(item) }}</span>
                <small>{{ formatRecordDate(item.created_at) }}</small>
              </div>
            </div>
            <div class="fund-record-value">
              <strong :class="fundRecordType">
                {{ fundRecordType === "withdraw" ? "-" : "+" }}{{ formatMoney(item.money) }} USD
              </strong>
              <span :class="`status-${Number(item.status)}`">
                {{ fundRecordStatus(item.status) }}
              </span>
            </div>
            <p v-if="item.reply" class="fund-record-reply">客服回复：{{ item.reply }}</p>
          </article>
        </div>

        <div v-else-if="fundRecordLoading" class="fund-record-empty">正在加载记录...</div>
        <div v-else-if="fundRecordLoadError" class="fund-record-empty">
          <span>记录加载失败</span>
          <button type="button" @click="fetchFundRecords(true)">重新加载</button>
        </div>
        <div v-else class="fund-record-empty">暂无团队成员{{ fundRecordType === "recharge" ? "充值" : "提现" }}记录</div>

        <button
          v-if="fundRecords.length && !fundRecordFinished"
          type="button"
          class="fund-record-more"
          :disabled="fundRecordLoading"
          @click="fetchFundRecords(false)"
        >
          {{ fundRecordLoading ? "加载中..." : "加载更多" }}
        </button>
      </section>

      <section v-if="root" class="member-section">
        <div class="section-title">
          <div>
            <span>TEAM MEMBERS</span>
            <h2>团队成员</h2>
          </div>
          <em>共 {{ teamMembers.length }} 人</em>
        </div>

        <div v-if="teamMembers.length" class="member-list">
          <article
            v-for="item in teamMembers"
            :key="item.id"
            :class="['member-card', { root: Number(item.id) === Number(root.id) }]"
            :style="{ marginLeft: generationIndent(item.generation) }"
            @click="loadRoot(item.id)"
          >
            <div class="member-top">
              <div class="member-avatar">{{ avatarText(item) }}</div>
              <div class="member-identity">
                <div>
                  <span class="generation-badge">{{ chineseGeneration(teamGeneration(item)) }}</span>
                  <span :class="['role-badge', { agent: Number(item.sale) === 1 }]">
                    {{ roleText(item) }}
                  </span>
                </div>
                <h3>{{ memberName(item) }}</h3>
                <p>{{ accountText(item) }}</p>
              </div>
              <span class="arrow">›</span>
            </div>

            <div class="member-meta">
              <div><span>邀请码</span><strong>{{ item.invite || "-" }}</strong></div>
              <div><span>直属下级</span><strong>{{ item.direct_count || 0 }}</strong></div>
              <div><span>全部下级</span><strong>{{ item.descendant_count || 0 }}</strong></div>
            </div>
            <div class="member-money">
              <div>
                <span>累计投资</span>
                <strong>{{ formatMoney(item.investment) }} USD</strong>
              </div>
              <div>
                <span>累计充值</span>
                <strong>{{ formatMoney(item.recharges) }} USD</strong>
              </div>
              <div>
                <span>累计提现</span>
                <strong class="withdraw">{{ formatMoney(item.withdraws) }} USD</strong>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="!loading && !root" class="empty-card">
        <div>未找到成员</div>
        <p>{{ emptyMessage }}</p>
        <button @click="resetTeam">返回我的团队</button>
      </section>

      <div v-if="loading && !root" class="loading-card">正在加载团队信息...</div>
    </main>
  </div>
</template>

<script>
import request from "@utils/request";

const emptySummary = () => ({
  direct_count: 0,
  descendant_count: 0,
  agent_count: 0,
  user_count: 0,
  max_generation: 0
});

const emptyEarningsSummary = () => ({
  total_investment: 0,
  total_commission: 0,
  generations: []
});

const emptyFundRecordSummary = () => ({
  record_count: 0,
  approved_amount: 0
});

export default {
  name: "Team",
  data() {
    return {
      keyword: "",
      loading: false,
      root: null,
      rows: [],
      summary: emptySummary(),
      earningsSummary: emptyEarningsSummary(),
      fundRecordType: "recharge",
      fundRecords: [],
      fundRecordSummary: emptyFundRecordSummary(),
      fundRecordPage: 1,
      fundRecordFinished: false,
      fundRecordLoading: false,
      fundRecordLoadError: false,
      scopeOwnerId: 0,
      emptyMessage: "仅支持查询您本人及您的下级成员"
    };
  },
  created() {
    this.fetchTeam({});
  },
  methods: {
    async fetchTeam(params) {
      this.loading = true;
      try {
        const response = await request.post(
          "/user/team/tree",
          params,
          { login: true }
        );
        const payload = (response.data && response.data.data) || {};
        this.root = payload.root || null;
        this.rows = Array.isArray(payload.rows) ? payload.rows : [];
        this.summary = Object.assign(emptySummary(), payload.summary || {});
        this.earningsSummary = Object.assign(
          emptyEarningsSummary(),
          payload.earnings_summary || {}
        );
        this.scopeOwnerId = Number(payload.scope_owner_id || this.scopeOwnerId || 0);
        this.emptyMessage =
          response.data.msg || "未在您的团队中找到该账号或邀请码";
        if (this.root) {
          await this.fetchFundRecords(true);
        } else {
          this.resetFundRecords();
        }
      } catch (error) {
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.msg
            ? error.response.data.msg
            : "团队信息加载失败，请稍后重试";
        this.$dialog.error(message);
      } finally {
        this.loading = false;
      }
    },
    searchTeam() {
      if (this.loading || this.fundRecordLoading) return;
      if (!this.keyword) {
        this.resetTeam();
        return;
      }
      this.root = null;
      this.rows = [];
      this.earningsSummary = emptyEarningsSummary();
      this.fetchTeam({ keyword: this.keyword });
    },
    loadRoot(id) {
      if (!id || this.loading || this.fundRecordLoading) return;
      this.keyword = "";
      this.root = null;
      this.rows = [];
      this.earningsSummary = emptyEarningsSummary();
      this.fetchTeam({ root_id: Number(id) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetTeam() {
      this.keyword = "";
      this.root = null;
      this.rows = [];
      this.earningsSummary = emptyEarningsSummary();
      this.fetchTeam(
        this.scopeOwnerId ? { root_id: this.scopeOwnerId } : {}
      );
    },
    resetFundRecords() {
      this.fundRecords = [];
      this.fundRecordSummary = emptyFundRecordSummary();
      this.fundRecordPage = 1;
      this.fundRecordFinished = false;
      this.fundRecordLoading = false;
      this.fundRecordLoadError = false;
    },
    async fetchFundRecords(reset) {
      if (!this.root || this.fundRecordLoading) return;
      if (reset) {
        this.resetFundRecords();
      }
      this.fundRecordLoading = true;
      this.fundRecordLoadError = false;
      try {
        const response = await request.get(
          "/user/team/fund-records",
          {
            root_id: Number(this.root.id),
            type: this.fundRecordType,
            page: this.fundRecordPage,
            limit: 10
          },
          { login: true }
        );
        const payload = (response.data && response.data.data) || {};
        const records = Array.isArray(payload.list) ? payload.list : [];
        this.fundRecords = reset
          ? records
          : this.fundRecords.concat(records);
        this.fundRecordSummary = Object.assign(
          emptyFundRecordSummary(),
          payload.summary || {}
        );
        this.fundRecordFinished = !payload.has_more;
        if (payload.has_more) {
          this.fundRecordPage += 1;
        }
      } catch (error) {
        this.fundRecordLoadError = true;
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.msg
            ? error.response.data.msg
            : "团队资金记录加载失败";
        this.$dialog.error(message);
      } finally {
        this.fundRecordLoading = false;
      }
    },
    switchFundRecordType(type) {
      if (type === this.fundRecordType || this.fundRecordLoading) return;
      this.fundRecordType = type;
      this.fetchFundRecords(true);
    },
    fundRecordStatus(status) {
      const labels = ["待审核", "已完成", "已拒绝", "已忽略"];
      return labels[Number(status)] || "未知状态";
    },
    formatRecordDate(value) {
      const timestamp = Number(value || 0);
      if (!timestamp) return "-";
      const date = new Date(timestamp * 1000);
      const pad = number => (Number(number) < 10 ? `0${number}` : String(number));
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    memberName(item) {
      return item.realname || item.username || item.mobile || "团队成员";
    },
    accountText(item) {
      return item.username || item.mobile || item.email || `UID ${item.id}`;
    },
    avatarText(item) {
      return this.memberName(item).slice(0, 1).toUpperCase();
    },
    roleText(item) {
      return Number(item.sale) === 1 ? "代理" : "用户";
    },
    teamGeneration(item) {
      return Math.max(1, Number(item.generation || 2) - 1);
    },
    chineseGeneration(level) {
      const names = ["", "一代", "二代", "三代", "四代", "五代"];
      return names[Number(level)] || `${Number(level) || 0}代`;
    },
    formatRate(value) {
      const number = Number(value || 0);
      return Number.isFinite(number)
        ? number.toLocaleString("zh-CN", { maximumFractionDigits: 2 })
        : "0";
    },
    generationIndent(generation) {
      const depth = Math.max(0, Math.min(Number(generation || 2) - 2, 3));
      return `${depth * 8}px`;
    },
    formatMoney(value) {
      const number = Number(value || 0);
      return Number.isFinite(number)
        ? number.toLocaleString("zh-CN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        : "0.00";
    },
    onClickLeft() {
      this.$router.go(-1);
    }
  },
  computed: {
    teamMembers() {
      if (!this.root) return [];
      return this.rows.filter(
        item => Number(item.id) !== Number(this.root.id)
      );
    },
    generationEarnings() {
      const source = Array.isArray(this.earningsSummary.generations)
        ? this.earningsSummary.generations
        : [];
      return [1, 2, 3].map(level => {
        return (
          source.find(item => Number(item.level) === level) || {
            level,
            people: 0,
            investment: 0,
            rate: 0,
            commission: 0
          }
        );
      });
    }
  }
};
</script>

<style scoped>
.team-page {
  min-height: 100vh;
  padding-bottom: 30px;
  color: #172b43;
  background:
    radial-gradient(circle at 85% 4%, rgba(123, 184, 226, 0.25), transparent 25%),
    linear-gradient(180deg, #edf4fa 0, #f7f9fc 42%, #f3f6fa 100%);
}

.team-content {
  width: min(100%, 470px);
  margin: 0 auto;
  padding: 14px;
  box-sizing: border-box;
}

.hero-card {
  padding: 24px 18px 18px;
  color: #fff;
  background:
    radial-gradient(circle at 85% 20%, rgba(114, 184, 232, 0.2), transparent 30%),
    linear-gradient(135deg, #071f3b, #104e7f 58%, #1769a6);
  border-radius: 23px;
  box-shadow: 0 15px 32px rgba(80, 43, 135, 0.2);
}

.eyebrow,
.section-title span {
  color: #f7d986;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.8px;
}

.hero-card h1 {
  margin: 6px 0 4px;
  font-size: 25px;
}

.hero-card > p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 1.6;
}

.search-box {
  display: flex;
  gap: 7px;
  margin-top: 17px;
  padding: 5px;
  background: #fff;
  border-radius: 13px;
}

.search-box input {
  min-width: 0;
  flex: 1;
  height: 38px;
  padding: 0 9px;
  color: #344a61;
  font-size: 12px;
  background: transparent;
  border: 0;
  outline: 0;
}

.search-box button {
  flex: 0 0 68px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, #123b68, #2587ce);
  border: 0;
  border-radius: 10px;
}

.scope-tip {
  margin-top: 8px !important;
  color: #f4deaa !important;
  font-size: 10px !important;
}

.root-card,
.earnings-section,
.fund-record-section,
.member-section,
.empty-card,
.loading-card {
  margin-top: 13px;
  background: #fff;
  border: 1px solid #e4ebf2;
  border-radius: 19px;
  box-shadow: 0 10px 25px rgba(68, 46, 100, 0.07);
}

.root-card {
  padding: 16px;
}

.root-main {
  display: flex;
  gap: 12px;
  align-items: center;
}

.avatar,
.member-avatar {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #fff;
  font-weight: 800;
  background: linear-gradient(145deg, #123b68, #2587ce);
  border-radius: 50%;
}

.avatar {
  width: 54px;
  height: 54px;
  font-size: 21px;
  box-shadow: 0 7px 16px rgba(111, 64, 164, 0.22);
}

.root-card h2 {
  margin: 6px 0 3px;
  color: #352943;
  font-size: 18px;
}

.root-card p {
  margin: 2px 0;
  color: #847c8d;
  font-size: 11px;
}

.root-badges,
.member-identity > div {
  display: flex;
  gap: 5px;
}

.generation-badge,
.role-badge,
.self-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 0 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
}

.generation-badge {
  color: #1769b0;
  background: #e7f2fb;
}

.role-badge {
  color: #337476;
  background: #e2f4f1;
}

.role-badge.agent {
  color: #a56a13;
  background: #fff0d2;
}

.self-badge {
  color: #a44c77;
  background: #f9e4ef;
}

.back-self {
  width: 100%;
  height: 35px;
  margin-top: 13px;
  color: #71409c;
  font-size: 11px;
  font-weight: 700;
  background: #edf4fa;
  border: 0;
  border-radius: 10px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  margin-top: 13px;
}

.summary-grid div {
  padding: 12px 3px;
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e5ebf2;
  border-radius: 14px;
}

.summary-grid span {
  display: block;
  color: #968e9e;
  font-size: 9px;
  white-space: nowrap;
}

.summary-grid strong {
  display: block;
  margin-top: 5px;
  color: #684094;
  font-size: 16px;
}

.earnings-section {
  margin-top: 13px;
  padding: 17px 12px 14px;
}

.fund-record-section {
  padding: 17px 12px 14px;
}

.fund-record-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 12px;
  background: linear-gradient(145deg, #edf5fb, #f8fbfd);
  border: 1px solid #dceaf5;
  border-radius: 13px;
}

.fund-record-summary span {
  color: #788798;
  font-size: 10px;
}

.fund-record-summary strong {
  color: #155f9f;
  font-size: 14px;
}

.fund-record-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 5px;
  margin-bottom: 10px;
  padding: 4px;
  background: #edf2f6;
  border-radius: 12px;
}

.fund-record-tabs button {
  height: 35px;
  color: #778698;
  font-size: 11px;
  font-weight: 700;
  background: transparent;
  border: 0;
  border-radius: 9px;
}

.fund-record-tabs button.active {
  color: #1769b0;
  background: #fff;
  box-shadow: 0 4px 12px rgba(27, 73, 115, 0.09);
}

.fund-record-tabs button:disabled {
  cursor: default;
}

.fund-record-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  margin-bottom: 8px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5ecf2;
  border-radius: 14px;
}

.fund-record-main {
  display: flex;
  min-width: 0;
  align-items: center;
}

.fund-record-main .member-avatar {
  width: 36px;
  height: 36px;
  margin-right: 9px;
  font-size: 14px;
}

.fund-record-member {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.fund-record-member strong,
.fund-record-member span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fund-record-member strong {
  color: #34485e;
  font-size: 12px;
}

.fund-record-member span {
  margin-top: 2px;
  color: #7e8b99;
  font-size: 9px;
}

.fund-record-member small {
  margin-top: 4px;
  color: #a0aab5;
  font-size: 8px;
}

.fund-record-value {
  text-align: right;
}

.fund-record-value strong,
.fund-record-value span {
  display: block;
  white-space: nowrap;
}

.fund-record-value strong {
  color: #16836b;
  font-size: 11px;
}

.fund-record-value strong.withdraw {
  color: #ac514b;
}

.fund-record-value span {
  width: fit-content;
  margin: 6px 0 0 auto;
  padding: 3px 7px;
  color: #98701d;
  font-size: 8px;
  background: #fff3d7;
  border-radius: 999px;
}

.fund-record-value .status-1 {
  color: #237766;
  background: #e3f4ef;
}

.fund-record-value .status-2,
.fund-record-value .status-3 {
  color: #8c6670;
  background: #f3e9ec;
}

.fund-record-reply {
  grid-column: 1 / -1;
  margin: 0;
  padding-top: 8px;
  color: #7f8d9b;
  font-size: 9px;
  line-height: 1.5;
  border-top: 1px dashed #dfe7ee;
}

.fund-record-empty {
  padding: 24px 10px;
  color: #96a1ad;
  font-size: 10px;
  text-align: center;
}

.fund-record-empty span {
  display: block;
  margin-bottom: 9px;
}

.fund-record-empty button,
.fund-record-more {
  color: #1769b0;
  font-size: 10px;
  background: #e8f2fa;
  border: 0;
  border-radius: 999px;
}

.fund-record-empty button {
  padding: 7px 15px;
}

.fund-record-more {
  display: block;
  width: 100%;
  height: 34px;
  margin-top: 5px;
}

.earnings-total {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.earnings-total > div {
  padding: 13px 11px;
  background: linear-gradient(145deg, #edf5fb, #f8fbfd);
  border: 1px solid #dceaf5;
  border-radius: 13px;
}

.earnings-total span,
.generation-data span {
  color: #788798;
  font-size: 9px;
}

.earnings-total strong {
  display: block;
  margin-top: 6px;
  color: #155f9f;
  font-size: 14px;
}

.generation-income-card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  margin-top: 8px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5ecf2;
  border-radius: 14px;
}

.generation-name {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 9px;
  border-right: 1px solid #e2e9f0;
}

.generation-name strong {
  color: #1c669f;
  font-size: 17px;
}

.generation-name span {
  margin-top: 5px;
  color: #8a96a3;
  font-size: 8px;
  line-height: 1.4;
}

.generation-data {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px 7px;
}

.generation-data p {
  min-width: 0;
}

.generation-data strong {
  display: block;
  margin-top: 3px;
  overflow: hidden;
  color: #33495f;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.generation-data .profit strong {
  color: #b77b20;
}

.earnings-note {
  margin: 11px 2px 0;
  color: #8b97a4;
  font-size: 9px;
  line-height: 1.6;
}

.member-section {
  padding: 17px 12px 12px;
}

.section-title {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 3px 12px;
}

.section-title h2 {
  margin: 4px 0 0;
  color: #382d45;
  font-size: 18px;
}

.section-title em {
  color: #9a929f;
  font-size: 10px;
  font-style: normal;
}

.member-list {
  overflow: hidden;
}

.member-card {
  position: relative;
  margin-bottom: 9px;
  padding: 13px;
  background: #f8fafc;
  border: 1px solid #e6edf3;
  border-radius: 15px;
  transition: transform 0.15s ease;
}

.member-card:active {
  transform: scale(0.985);
}

.member-card.root {
  background: linear-gradient(135deg, #eaf3fb, #fff 72%);
  border-color: #d6e7f3;
}

.member-top {
  display: flex;
  align-items: center;
}

.member-avatar {
  width: 38px;
  height: 38px;
  margin-right: 9px;
  font-size: 15px;
}

.member-identity {
  min-width: 0;
  flex: 1;
}

.member-identity h3 {
  margin: 5px 0 1px;
  overflow: hidden;
  color: #403548;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-identity p {
  margin: 0;
  overflow: hidden;
  color: #8e8695;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow {
  color: #aa9db5;
  font-size: 24px;
}

.member-meta {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 0.8fr;
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid #eeeaf3;
}

.member-meta div {
  text-align: center;
  border-right: 1px solid #eeeaf3;
}

.member-meta div:last-child {
  border-right: 0;
}

.member-meta span {
  display: block;
  color: #9a929f;
  font-size: 9px;
}

.member-meta strong {
  display: block;
  margin-top: 4px;
  color: #5b476c;
  font-size: 11px;
}

.member-money {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
  margin-top: 10px;
}

.member-money div {
  min-width: 0;
  padding: 8px 4px;
  text-align: center;
  background: #f0f5f9;
  border-radius: 9px;
}

.member-money span,
.member-money strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-money span {
  color: #8f99a5;
  font-size: 8px;
}

.member-money strong {
  margin-top: 4px;
  color: #1769b0;
  font-size: 9px;
}

.member-money strong.withdraw {
  color: #a85c57;
}

.empty-card,
.loading-card {
  padding: 38px 20px;
  color: #8e8596;
  text-align: center;
}

.empty-card div {
  color: #4e4059;
  font-size: 17px;
  font-weight: 700;
}

.empty-card p {
  margin: 8px 0 16px;
  font-size: 11px;
}

.empty-card button {
  padding: 9px 18px;
  color: #fff;
  font-size: 11px;
  background: #1769b0;
  border: 0;
  border-radius: 999px;
}

@media (max-width: 360px) {
  .summary-grid {
    gap: 4px;
  }

  .summary-grid span {
    font-size: 8px;
  }

  .member-money {
    gap: 3px;
  }
}
</style>
